/**
 * Scores, on the client side.
 *
 * The API stores and sends integers from 0 to 100; a person reads 0.0 to 10.0.
 * The conversion is here rather than on the server because a slider that had
 * to ask what 8.7 looks like would be a slider nobody enjoys dragging.
 *
 * That duplicates `api/app/modules/ratings/scores.py`, deliberately and
 * narrowly. `test_the_web_client_agrees_about_the_representation` reads this
 * file and fails if the two ever disagree about the range or the bucket width.
 */
import { plural } from './format.js'

export const SCORE_MIN = 0
export const SCORE_MAX = 100
export const BUCKET_WIDTH = 5
export const HISTOGRAM_BUCKETS = (SCORE_MAX - SCORE_MIN) / BUCKET_WIDTH + 1

/** 87 → "8.7". Always one decimal, so a column of scores lines up. */
export function display(score) {
  if (score == null) return null
  const n = clamp(Math.round(score))
  return `${Math.floor(n / 10)}.${n % 10}`
}

export function clamp(score) {
  return Math.min(SCORE_MAX, Math.max(SCORE_MIN, score))
}

/**
 * "8.7" → 87, and null for anything that is not a score.
 *
 * Refuses more than one decimal place rather than rounding it: a field that
 * silently turned 8.75 into 8.8 would be lying about what it stored.
 */
export function parse(text) {
  // One comma is a decimal point (Q15, Josh 2026-09-24): with one decimal
  // place and a 10.0 ceiling it cannot be a thousands separator. `scores.py`
  // does the same.
  const raw = String(text ?? '').trim().replace(',', '.')
  if (!raw) return null
  const match = /^(\d*)(?:\.(\d?))?$/.exec(raw)
  if (!match) return null
  const [, whole, fraction] = match
  // Nothing either side of the point is not a score — `scores.py` refuses a
  // lone "." too, and before the comma rule this was where the twins parted.
  if (!whole && !fraction) return null
  const score = Number(whole || 0) * 10 + Number(fraction || 0)
  return score >= SCORE_MIN && score <= SCORE_MAX ? score : null
}

/** The score a histogram bucket is centred on — what its axis label means. */
export function bucketScore(index) {
  return index * BUCKET_WIDTH
}

/** "Blended", for a method the API calls `blended`. */
export const METHOD_LABELS = {
  direct: 'Direct',
  track_average: 'Track average',
  blended: 'Blended',
}

export const STATE_LABELS = {
  first_impression: 'First impression',
  developing: 'Developing',
  settled: 'Settled',
  retrospective: 'Retrospective',
}

/**
 * Labels for the sub-score dimensions (1B-4b Phase 1, DEC-2026-08-04-040).
 *
 * The *vocabulary* comes from `GET /rating-dimensions`, not from here — a
 * hardcoded list would go stale against `SUBSCORE_SCHEMA_VERSION`. This is
 * only how each slug is spelled for a person, and `dimensionLabel` falls back
 * to the slug itself, so a dimension added server-side renders as words rather
 * than as nothing.
 */
export const DIMENSION_LABELS = {
  production: 'Production',
  songwriting: 'Songwriting',
  lyrics: 'Lyrics',
  performance: 'Performance',
  cohesion: 'Cohesion',
  innovation: 'Innovation',
  replay_value: 'Replay value',
  personal_enjoyment: 'Personal enjoyment',
  hook: 'Hook',
}

export function dimensionLabel(slug) {
  if (DIMENSION_LABELS[slug]) return DIMENSION_LABELS[slug]
  const words = String(slug).replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const SUBSCORE_MODE_LABELS = {
  independent: 'Descriptive',
  auto_average: 'Average',
  weighted: 'Weighted',
}

/** What each mode does to the score above, in one sentence each. */
export const SUBSCORE_MODE_HELP = {
  independent: 'Your dimensions describe this score. They do not change it.',
  auto_average: 'This score follows the average of the dimensions you fill in.',
  weighted: 'This score follows the dimensions, weighted the way you set below.',
}

/**
 * Tag family headings (1B-4b Phase 2a).
 *
 * The words a tag *is* come from the server — they are rows, and the owner
 * adopts new ones without a deploy. The four family names do not: they are the
 * brief's own categories, they are headings rather than data, and sending
 * "Where and how you listened" over the wire on every open would be shipping
 * copy through an API. `familyLabel` falls back to the slug, so a fifth family
 * added server-side renders as a word rather than as nothing.
 */
export const TAG_FAMILY_LABELS = {
  musical: 'The music',
  mood: 'The mood',
  context: 'Where and how you listened',
  reaction: 'How it landed',
}

export function familyLabel(slug) {
  if (TAG_FAMILY_LABELS[slug]) return TAG_FAMILY_LABELS[slug]
  const words = String(slug).replace(/[-_]/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * A tag slug, spelled for a person, without asking what it is called today.
 *
 * The picker uses the server's `label` and should. This is for the places that
 * hold a slug and nothing else — a revision from last year, which may name a
 * tag the vocabulary has since retired. Looking that one up would render a
 * hole where the honest answer is "whatever it was called then", so the slug
 * is tidied rather than resolved: `late-night` reads as "Late night".
 *
 * That lands on the seeded label exactly for all but two words — `bass-heavy`
 * and `skip-heavy` are labelled with the hyphen kept and render here without
 * it. Keeping every hyphen instead would spell the other nine wrong, so this
 * is the side of the trade with fewer casualties, not a free choice.
 */
export function tagLabel(slug) {
  const words = String(slug).replace(/[-_]/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * DEC-2026-08-01-004's coverage line, in words.
 *
 * Two different sentences, because "rated 5 of 11" on its own reads like a
 * failure rather than like progress towards something.
 */
export function coverageLabel(average) {
  if (!average || !average.primary_total) return 'No tracklist to rate yet'
  const { primary_rated: rated, primary_total: total, needed } = average
  if (rated >= needed) return `Rated ${rated} of ${total} ${plural(total, 'track')}`
  return `Rated ${rated} of ${total} — ${needed} for an average`
}
